// @flow
import {useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import queryString from 'query-string';

import {getAuthDefaultToken, useUser} from '../modules/auth/utils';
import {useStoreInfo, useIsDowngradeTier} from '../modules/store/utils';
import {useIsInEndSubscriptionDays} from '../modules/tiers/utils';
import {usePrevious} from '../utils/react';
import {useActions} from '../utils/globalStore';
import {actions as AuthActions} from '../modules/auth/duck';
import {actions as StoreActions} from '../modules/store/duck';
import type {RootState} from '../modules';

const isAuthenticatingSelector = (state: RootState) =>
  state.auth.isAuthenticating;
const initialAuthDoneSelector = (state: RootState) =>
  state.auth.initialAuthDone;

export function useAppInitialization() {
  const history = useHistory();
  const location = useLocation();
  const queryObject = queryString.parse(history.location.search);

  const user = useUser();
  const store = useStoreInfo();
  const isAuthenticating = useSelector(isAuthenticatingSelector);
  const initialAuthDone = useSelector(initialAuthDoneSelector);

  const getSession = useActions(AuthActions.getSession);
  const fetchStore = useActions(StoreActions.fetchStore);

  const prevStore = usePrevious(store);
  const prevUser = usePrevious(user);

  const isRemaining7days = useIsInEndSubscriptionDays(7);
  const isDowngradeTier = useIsDowngradeTier();

  useEffect(() => {
    if (!user && !store && !isAuthenticating && !initialAuthDone) {
      const token = getAuthDefaultToken(queryObject);
      getSession(token ? {token} : undefined);
    }
  }, [getSession, initialAuthDone, isAuthenticating, queryObject, store, user]);

  // 2. Fetch store after user login
  useEffect(() => {
    if (!prevUser && user && document?.location?.hostname) {
      fetchStore({domain: document.location.hostname});
    }
  }, [fetchStore, prevUser, user]);

  // 3. Redirect to tiers-list if subscription ending or downgraded
  useEffect(() => {
    if (!prevStore && store) {
      if (isRemaining7days || isDowngradeTier) {
        history.push('/tiers-list');
      }
    }
  }, [history, isRemaining7days, isDowngradeTier, prevStore, store]);

  // 4. Clean s_u_token from URL after auth
  useEffect(() => {
    if (!prevUser && user && queryObject.s_u_token) {
      const {s_u_token, ...qp} = queryObject;
      history.replace({
        pathname: location.pathname,
        search: `?${queryString.stringify(qp)}`,
      });
    }
  }, [history, location.pathname, prevUser, queryObject, user]);

  return {user, store, isAuthenticating, initialAuthDone};
}
